import { useState, useMemo } from 'react';
import { Search, Check } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Lista icone comuni per servizi fotografici
const COMMON_ICONS = [
  'Camera', 'Video', 'Image', 'Images', 'Film', 'Clapperboard',
  'Aperture', 'Focus', 'Scan', 'ScanLine', 'Plane', 'Mountain',
  'Heart', 'Star', 'Award', 'Trophy', 'Medal', 'Crown',
  'Users', 'User', 'Baby', 'PartyPopper', 'Cake', 'Gift',
  'Building', 'Building2', 'Briefcase', 'GraduationCap',
  'Palette', 'Sparkles', 'Wand2', 'Lightbulb', 'Zap',
  'Clock', 'Calendar', 'MapPin', 'Navigation', 'Compass',
  'Sun', 'Moon', 'CloudSun', 'Sunset', 'Sunrise'
];

// Ottieni tutte le icone disponibili
const getAllIcons = () => {
  return Object.keys(LucideIcons).filter(key => 
    typeof LucideIcons[key] === 'function' && 
    key !== 'createLucideIcon' &&
    key !== 'default' &&
    !key.startsWith('Lucide')
  );
};

export function getIconComponent(iconName) {
  if (!iconName) return LucideIcons.Image;
  return LucideIcons[iconName] || LucideIcons.Image;
}

export default function IconPicker({ value, onChange, className }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  
  const allIcons = useMemo(() => getAllIcons(), []);
  
  const filteredIcons = useMemo(() => {
    if (!search) return COMMON_ICONS;
    const searchLower = search.toLowerCase();
    return allIcons.filter(icon => 
      icon.toLowerCase().includes(searchLower)
    ).slice(0, 50);
  }, [search, allIcons]);

  const IconComponent = getIconComponent(value);

  const handleSelect = (iconName) => {
    onChange(iconName);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className={cn('w-full justify-start gap-2', className)}
        >
          <IconComponent size={18} />
          <span className="flex-1 text-left truncate">
            {value || 'Seleziona icona'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              placeholder="Cerca icona..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-2">
          <div className="grid grid-cols-6 gap-1">
            {filteredIcons.map((iconName) => {
              const Icon = LucideIcons[iconName];
              if (!Icon) return null;
              const isSelected = value === iconName;
              return (
                <button
                  key={iconName}
                  onClick={() => handleSelect(iconName)}
                  className={cn(
                    'p-2 rounded hover:bg-muted flex items-center justify-center relative',
                    isSelected && 'bg-primary text-primary-foreground hover:bg-primary'
                  )}
                  title={iconName}
                >
                  <Icon size={20} />
                  {isSelected && (
                    <Check size={10} className="absolute bottom-0.5 right-0.5" />
                  )}
                </button>
              );
            })}
          </div>
          {filteredIcons.length === 0 && (
            <p className="text-center text-muted-foreground py-4 text-sm">
              Nessuna icona trovata
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
